/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CalculateLineItemValueModResult } from '../models/CalculateLineItemValueModResult';
import type { CalculateLineItemValuesPayload } from '../models/CalculateLineItemValuesPayload';
import type { CreateStatementPayload } from '../models/CreateStatementPayload';
import type { MappingPropertyRead } from '../models/MappingPropertyRead';
import type { StatementRead } from '../models/StatementRead';
import type { StatementReport } from '../models/StatementReport';
import type { StatementUpdate } from '../models/StatementUpdate';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class FinancialsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Create Statement
     * @param entityId
     * @param requestBody
     * @returns StatementRead Successful Response
     * @throws ApiError
     */
    public createStatement(
        entityId: string,
        requestBody: CreateStatementPayload,
    ): CancelablePromise<StatementRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/financials/create-statement',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Statement
     * @param entityId
     * @param statementId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateStatement(
        entityId: string,
        statementId: string,
        requestBody: StatementUpdate,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/financials/update-statement/{statement_id}',
            path: {
                'entity_id': entityId,
                'statement_id': statementId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Statement
     * @param entityId
     * @param statementId
     * @param deleteDependencies
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteStatement(
        entityId: string,
        statementId: string,
        deleteDependencies: boolean = false,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/entities/{entity_id}/financials/delete-statement/{statement_id}',
            path: {
                'entity_id': entityId,
                'statement_id': statementId,
            },
            query: {
                'delete_dependencies': deleteDependencies,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Statements
     * @param entityId
     * @returns StatementRead Successful Response
     * @throws ApiError
     */
    public listStatements(
        entityId: string,
    ): CancelablePromise<Array<StatementRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/financials/list-statements',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Statement For Entity
     * @param entityId
     * @param statementId
     * @param startDate
     * @param endDate
     * @returns StatementReport Successful Response
     * @throws ApiError
     */
    public getStatementForEntity(
        entityId: string,
        statementId: string,
        startDate?: string,
        endDate?: string,
    ): CancelablePromise<StatementReport> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/financials/get-statement/{statement_id}',
            path: {
                'entity_id': entityId,
                'statement_id': statementId,
            },
            query: {
                'start_date': startDate,
                'end_date': endDate,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Effective Date Range For Statement
     * @param entityId
     * @param statementId
     * @returns string Successful Response
     * @throws ApiError
     */
    public getEffectiveDateRangeForStatement(
        entityId: string,
        statementId: string,
    ): CancelablePromise<Array<string>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/financials/get-latest-effective-date-for-statement/{statement_id}',
            path: {
                'entity_id': entityId,
                'statement_id': statementId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Export Statement For Entity Id
     * @param entityId
     * @param statementId
     * @param startDate
     * @param endDate
     * @returns string Successful Response
     * @throws ApiError
     */
    public exportStatementForEntityId(
        entityId: string,
        statementId: string,
        startDate?: string,
        endDate?: string,
    ): CancelablePromise<Record<string, string>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/financials/export-statement',
            path: {
                'entity_id': entityId,
            },
            query: {
                'statement_id': statementId,
                'start_date': startDate,
                'end_date': endDate,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Calculate Line Item Values
     * @param entityId
     * @param statementId
     * @param requestBody
     * @returns CalculateLineItemValueModResult Successful Response
     * @throws ApiError
     */
    public calculateLineItemValues(
        entityId: string,
        statementId: string,
        requestBody: CalculateLineItemValuesPayload,
    ): CancelablePromise<Record<string, CalculateLineItemValueModResult>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/financials/calculate-line-item-values/{statement_id}',
            path: {
                'entity_id': entityId,
                'statement_id': statementId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Mappings By Line Item Id
     * @param entityId
     * @param statementId
     * @returns MappingPropertyRead Successful Response
     * @throws ApiError
     */
    public listMappingsByLineItemId(
        entityId: string,
        statementId: string,
    ): CancelablePromise<Record<string, Array<MappingPropertyRead>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/financials/list_mappings_by_line_item_id/{statement_id}',
            path: {
                'entity_id': entityId,
                'statement_id': statementId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
